import * as React from 'react';
import { jc, jcm } from 'utils';
import { Breakpoint } from 'types/utils';
import { memo } from 'common/hocs';
import { Section } from 'features/page-navigation/types';
import { Dispatch } from 'redux';
import { Styles } from './sticky-panel.styles';
import { Navigation } from './components/navigation';
import { AvailabilityState, ProductProps } from './components/types/product-props';
import { MobileProduct, TabletProduct, MediumPlusScreenProduct, ProductBlocks } from './components/product';

export type StickyPanelProps = {
  breakpoint: Breakpoint;
  sections: Array<{
    href: string;
    title: string;
    badgeNumber?: number;
  }>;
  activeSectionHref: string;
  onSectionActivated: (section: Section) => void;
  productImageUrl: string;
  productDescription: string;
  slots?: Partial<Record<'price' | 'image' | 'description' | 'basketItem', React.ReactNode>>;
  availabilityState: AvailabilityState;
  display?: 'full' | 'only-navigation' | 'only-product' | 'none';
  dispatch?: Dispatch;
  isSet?: boolean;
} & React.HTMLAttributes<HTMLElement>;

const StickyPanelComponent: React.ForwardRefRenderFunction<HTMLDivElement, StickyPanelProps> = ({
  breakpoint,
  sections,
  activeSectionHref,
  onSectionActivated,
  productImageUrl,
  productDescription,
  slots,
  availabilityState,
  display = 'full',
  className = '',
  dispatch,
  isSet,
  ...htmlProps
}) => {
  const BasketItemJsx = slots?.basketItem;

  const isNavigationHidden = display === 'only-product' || display === 'none';
  const isProductHidden = display === 'only-navigation' || display === 'none';

  const Image = slots?.image || (
    <ProductBlocks.Image
      description={productDescription}
      onClick={() => onSectionActivated({ title: 'Товар', href: activeSectionHref })}
      url={productImageUrl}
    />
  );
  const Description = slots?.description || <ProductBlocks.Description description={productDescription} />;

  let Price: React.ReactNode = null;
  if (availabilityState.availability === 'available') {
    if (slots?.price) {
      Price = slots.price;
    } else if (typeof availabilityState.price === 'number') {
      Price = (
        <ProductBlocks.Price
          onClick={() => onSectionActivated({ title: 'Цена', href: activeSectionHref })}
          price={availabilityState.price}
        />
      );
    }
  }

  const productProps: ProductProps = {
    slots: {
      image: Image,
      description: Description,
      price: Price,
    },
    availabilityState,
    BasketItem: BasketItemJsx,
    onSectionActivated,
  };

  return (
    <div
      className={jcm({
        [Styles.stickyPanelWrapper]: true,
        [Styles.stickyPanelMediumWrapper]: breakpoint === 'medium',
        [Styles.stickyPanelTabletWrapper]: breakpoint === 'tablet',
        [className]: true,
        [Styles.invisible]: display === 'none',
      })}
      {...htmlProps}
    >
      <div
        data-qa="sticky-panel-inner-wrapper"
        className={jc(Styles.stickyPanelInnerWrapper, breakpoint || '')}
      >
        <div
          data-qa="sticky-product-wrapper"
          className={jcm({
            [Styles.mediumPlusScreenProductWrapper]: true,
            [Styles.fullSize]: true,
            [Styles.hidden]: isNavigationHidden,
          })}
        >
          <Navigation
            sections={sections}
            activeSectionHref={activeSectionHref}
            onSectionActivated={onSectionActivated}
            isSet={isSet}
          />
        </div>
        {breakpoint === 'mobile' && (
          <div className={jcm({ [Styles.mobileProductWrapper]: true, [Styles.hidden]: isProductHidden })}>
            <MobileProduct {...productProps} />
          </div>
        )}
        {breakpoint === 'tablet' && (
          <div className={jcm({ [Styles.tabletProductWrapper]: true, [Styles.hidden]: isProductHidden })}>
            <TabletProduct {...productProps} />
          </div>
        )}
        {(breakpoint === 'medium' || breakpoint === 'large') && (
          <div
            className={jcm({
              [Styles.mediumPlusScreenProductWrapper]: true,
              [Styles.hidden]: isProductHidden,
            })}
          >
            <MediumPlusScreenProduct {...productProps} />
          </div>
        )}
      </div>
    </div>
  );
};
export const StickyPanel = memo(StickyPanelComponent);
